import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { adminAPI } from '@/api/client';
import { toast } from 'sonner';

const EmailQueueManager = () => {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [clearing, setClearing] = useState(false);

  const fetchQueue = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getEmailQueue();
      setQueue(response.data.emails || []);
    } catch (error) {
      console.error('Failed to fetch email queue:', error);
      toast.error('Failed to load email queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueue();
  }, []);

  const handleRetry = async () => {
    if (queue.length === 0) {
      toast.info('No failed emails to retry');
      return;
    }
    
    try {
      setRetrying(true);
      const response = await adminAPI.retryEmailQueue();
      toast.success(response.data.message || 'Retry completed');
      fetchQueue(); // Refresh queue
    } catch (error) {
      console.error('Failed to retry emails:', error);
      toast.error('Failed to retry emails');
    } finally {
      setRetrying(false);
    }
  };

  const handleClear = async () => {
    if (queue.length === 0) {
      toast.info('Queue is already empty');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to clear ${queue.length} failed emails? This cannot be undone.`)) {
      return;
    }
    
    try {
      setClearing(true);
      const response = await adminAPI.clearEmailQueue();
      toast.success(response.data.message || 'Queue cleared');
      setQueue([]);
    } catch (error) {
      console.error('Failed to clear queue:', error);
      toast.error('Failed to clear queue');
    } finally {
      setClearing(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString('en-BD', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  return (
    <div className="space-y-6 p-4">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            📧 Email Queue Manager
          </CardTitle>
          <CardDescription>
            View and retry failed welcome emails. Emails that fail after 3 retries are added here.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            {/* Stats */}
            <div className="flex-1">
              <div className="text-3xl font-bold text-orange-600">
                {loading ? '...' : queue.length}
              </div>
              <div className="text-sm text-gray-500">Failed Emails</div>
            </div>
            
            {/* Actions */}
            <div className="flex gap-2">
              <Button 
                onClick={fetchQueue} 
                variant="outline" 
                disabled={loading}
              >
                🔄 Refresh
              </Button>
              <Button 
                onClick={handleRetry} 
                disabled={retrying || queue.length === 0}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {retrying ? '⏳ Retrying...' : '🔁 Retry All'}
              </Button>
              <Button 
                onClick={handleClear} 
                variant="destructive"
                disabled={clearing || queue.length === 0}
              >
                {clearing ? '⏳ Clearing...' : '🗑️ Clear All'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Queue List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-500 mt-2">Loading queue...</p>
        </div>
      ) : queue.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-5xl mb-4">✅</div>
            <h3 className="text-lg font-semibold text-green-600">All Clear!</h3>
            <p className="text-gray-500">No failed emails in the queue</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Failed Emails ({queue.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {queue.map((email, index) => (
                <div 
                  key={index} 
                  className="p-4 bg-red-50 border border-red-200 rounded-lg"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium text-gray-800">
                        📩 {email.to_email}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        <strong>Subject:</strong> {email.subject?.substring(0, 50)}...
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        <strong>Failed at:</strong> {formatDate(email.failed_at)}
                      </div>
                      {email.error && (
                        <div className="text-xs text-red-600 mt-1">
                          <strong>Error:</strong> {email.error.substring(0, 100)}...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">ℹ️</span>
            <div className="text-sm text-blue-800">
              <p className="font-medium">How Email Retry Works:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Emails are automatically retried <strong>3 times</strong> with 5 second delays</li>
                <li>If still failing, they are added to this queue</li>
                <li>Click <strong>"Retry All"</strong> to attempt sending again</li>
                <li>Bulk student creation now has <strong>1.5s delay</strong> between emails</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailQueueManager;
